import React, { useState, useEffect, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { NotificationHistory, notificationsAPI } from '../../lib/api/notificationApi';
import { useNotifications } from './NotificationContext';

interface NotificationListProps {
    limit?: number;
    onNotificationClick?: (notification: NotificationHistory) => void;
    className?: string;
    screen?: string;
}


export const NotificationList: React.FC<NotificationListProps> = ({
    limit = 10,
    onNotificationClick,
    className = '',
    screen = 'notification-list'
}) => {
    const [notifications, setNotifications] = useState<NotificationHistory[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const { refreshNotifications } = useNotifications();
    const [error, setError] = useState<string | null>(null);
    const hasLoadedRef = React.useRef<boolean>(false);
    const [currentUserId, setCurrentUserId] = useState<string | null>(null);

    useEffect(() => {
        const fetchCurrentUserId = async () => {
            try {
                const id = await notificationsAPI.getCurrentUserId();
                setCurrentUserId(id);
            } catch (error) {
                console.error('Error al obtener el ID del usuario actual:', error);
            }
        };

        fetchCurrentUserId();
    }, []);

    const loadNotifications = useCallback(async () => {
        if (hasLoadedRef.current && !loading) return;

        setLoading(true);
        setError(null);

        try {
            const result = await notificationsAPI.getHistory(page, limit);
            console.log("Respuesta API:", result);

            // IMPORTANTE: La API devuelve 'data' en lugar de 'notifications'
            if (result && result.data) {
                setNotifications(result.data);

                if (result.pagination) {
                    setTotalPages(result.pagination.totalPages || 1);
                }

                console.log('Notificaciones cargadas:', result.data.length);
            } else {
                console.warn('La respuesta de notificaciones no tiene el formato esperado:', result);
                setNotifications([]);
                setTotalPages(1);
            }

            // Use ref instead of state
            hasLoadedRef.current = true;
        } catch (error) {
            console.error('Error al cargar notificaciones:', error);
            setError('No se pudieron cargar las notificaciones');
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    }, [page, limit]); // Removed 'loading' and 'hasLoaded' from dependencies

    // Cargar notificaciones
    useEffect(() => {
        loadNotifications();
    }, [page, loadNotifications]);

    // Manejar clic en notificación
    const handleNotificationClick = async (notification: NotificationHistory) => {
        try {
            const currentUserId = await notificationsAPI.getCurrentUserId();

            const isUnread = notification.analytics &&
                notification.analytics.openedBy &&
                !notification.analytics.openedBy.some(
                    open => open.userId === currentUserId
                );

            // Marcar como leída con información adicional
            await notificationsAPI.trackNotificationOpen(
                notification._id,
                {
                    action: 'click',
                    screen: screen,
                    actionTaken: notification.data?.route ? 'navigate' : 'view'
                }
            );

            // Actualizar estado local
            setNotifications(prevNotifications =>
                prevNotifications.map(n =>
                    n._id === notification._id ? {
                        ...n,
                        analytics: {
                            ...n.analytics,
                            openCount: ((n.analytics?.openCount || 0) + (isUnread ? 1 : 0)),
                            openedBy: [...(n.analytics?.openedBy || []), {
                                userId: currentUserId,
                                timestamp: new Date()
                            }]
                        }
                    } : n
                )
            );

            // Actualizar contador global - usar setTimeout para evitar ciclos
            setTimeout(() => {
                refreshNotifications();
            }, 0);

            // Llamar al callback si existe
            if (onNotificationClick) {
                onNotificationClick(notification);
            }
        } catch (error) {
            console.error('Error al procesar clic en notificación:', error);
        }
    };

    // Formatear fecha relativa
    const formatDate = (dateStr: string) => {
        try {
            return formatDistanceToNow(new Date(dateStr), {
                addSuffix: true,
                locale: es
            });
        } catch (e) {
            return 'fecha desconocida';
        }
    };

    // Obtener ícono según tipo
    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'goal':
            case 'milestone':
                return '🎯';
            case 'marketing':
                return '📣';
            case 'update':
                return '🔄';
            case 'activity':
                return '📋';
            case 'reminder':
                return '⏰';
            case 'completion':
                return '✅';
            case 'individual':
                return '👤';
            case 'group':
                return '👥';
            case 'topic':
                return '📢';
            default:
                return '📬';
        }
    };

    // Comprobar si ha sido leída
    const isNotificationRead = (notification: NotificationHistory): boolean => {

        if (!notification.analytics?.openedBy || notification.analytics.openedBy.length === 0) {
            return false;
        }

        const isRead = notification.analytics.openedBy.some(
            record => record.userId === currentUserId
        );

        return isRead;
    };

    if (loading && notifications.length === 0) {
        return (
            <div className={`p-4 text-center ${className}`}>
                <div className="animate-pulse">Cargando notificaciones...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`p-4 text-center text-red-500 ${className}`}>
                {error}
            </div>
        );
    }

    // Verificar explícitamente que notifications sea un array antes de verificar su longitud
    if (!notifications || notifications.length === 0) {
        return (
            <div className={`p-4 text-center text-gray-500 ${className}`}>
                No tienes notificaciones
            </div>
        );
    }

    return (
        <div className={`${className}`}>
            <ul className="divide-y divide-gray-200">
                {notifications.map((notification) => {
                    const isRead = isNotificationRead(notification);

                    return (
                        <li
                            key={notification._id}
                            className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${!isRead ? 'bg-blue-50' : ''}`}
                            onClick={() => handleNotificationClick(notification)}
                        >
                            <div className="flex items-start">
                                <div className="flex-shrink-0 mr-3 text-xl">
                                    {getNotificationIcon(notification.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium ${!isRead ? 'font-bold' : ''}`}>
                                        {notification.title}
                                    </p>
                                    <p className="text-sm text-gray-500 line-clamp-2">
                                        {notification.body}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        {formatDate(notification.sentAt || notification.createdAt)}
                                    </p>
                                </div>
                                {!isRead && (
                                    <div className="flex-shrink-0 ml-2">
                                        <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                                    </div>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>

            {totalPages > 1 && (
                <div className="flex justify-between items-center px-4 py-3 bg-gray-50">
                    <button
                        onClick={() => setPage(prev => Math.max(1, prev - 1))}
                        disabled={page === 1}
                        className={`px-3 py-1 rounded ${page === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
                    >
                        Anterior
                    </button>
                    <span className="text-sm">
                        Página {page} de {totalPages}
                    </span>
                    <button
                        onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={page === totalPages}
                        className={`px-3 py-1 rounded ${page === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
                    >
                        Siguiente
                    </button>
                </div>
            )}
        </div>
    );
};